import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import ChatBot from "../component/ChatBot";
import {
  IconLayoutDashboard,
  IconPackage,
  IconAdjustmentsHorizontal,
  IconChartBar,
  IconArrowsExchange,
  IconUser,
  IconMessage2,
  IconCurrencyDollar,
  IconMessageCircle,
  IconTrash,
  IconArrowBackUp,
  IconMapPin,
  IconShoppingCart,
  IconBuildingStore,
  IconCash,
  IconDatabase,
  IconFileUpload,
  IconFileDownload,
  IconUserCircle,
  IconShieldLock,
  IconDiscount2,
} from "@tabler/icons-react";
import SideNav from "./page/sidNav";
import TopNavSuperAdmin from "./page/topNav";
import "../css/sellerLayout.css";
import { usePreference } from "../context/PreferenceContext";
import RoleToggle from "../component/RoleToggle";

import Navbar from '../pages/Navbar';
import AuthServ from "../service/AuthServ";

const SellerLayout = () => {
  const { t } = usePreference();
  const [, setExpandedKeys] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSideNavOpen, setIsSideNavOpen] = useState(!isMobile);
  const location = useLocation();
  const navigate = useNavigate();

  // Sync user profile and token on layout mount to ensure fresh JWT role claim
  useEffect(() => {
    const syncProfile = async () => {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("customerToken");
        if (token) {
          // Force profile check to sync stale tokens if the user's role changed (e.g. approved by admin)
          const res = await AuthServ.single(true);
          const u = res?.data || res;
          if (u) {
            const role = (u.role || "").toLowerCase();
            localStorage.setItem("role", role);
            localStorage.setItem("user", JSON.stringify(u));
            window.dispatchEvent(new Event("auth-change"));
          }
        }
      } catch (err) {
        console.error("Failed to sync profile on seller layout mount:", err);
      }
    };
    syncProfile();
  }, []);

  const handleDashboardClick = () => {
    setExpandedKeys([]); // collapse all menus
    navigate("/seller/dashboard");
  };

  // ✅ Close sidenav on small screens
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsSideNavOpen(!mobile);
    };
    window.addEventListener("resize", handleResize);
    handleResize(); // run once initially
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Close sidenav when route changes (on mobile or tablet)
  useEffect(() => {
    if (isMobile) setIsSideNavOpen(false);
  }, [location.pathname, isMobile]);

  const toggleSideNav = () => setIsSideNavOpen((prev) => !prev);

  const sellerItems = [
    {
      icon: <IconLayoutDashboard size={20} />,
      label: t('dashboard'),
      key: "dashboard",
      path: "/seller/dashboard",
      onClick: handleDashboardClick,
    },
    {
      icon: <IconPackage size={20} />,
      label: t('shop'),
      key: "manage-seller-shop",
      children: [
        {
          icon: <IconBuildingStore size={18} />,
          label: t('shop_setup'),
          key: "shop-setup",
          path: "/seller/shop-setup",
        },
        // {
        //   icon: <IconSettings size={18} />,
        //   label: t('shop_settings'),
        //   key: "shop-settings",
        //   path: "/seller/shop-settings",
        // },
        {
          icon: <IconCash size={18} />,
          label: t('products_sold'),
          key: "product-sold",
          path: "/seller/product-sold",
        },
        {
          icon: <IconDatabase size={18} />,
          label: t('inventory_update'),
          key: "inventory-update",
          path: "/seller/inventory-update",
        },
        // cod-instructions removed
      ],
    },
    {
      icon: <IconPackage size={20} />,
      label: t('products'),
      key: "manage-seller-products",
      children: [
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('product_setup'),
          key: "product-setup",
          path: "/seller/product-setup",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('product_options'),
          key: "product-option",
          path: "/seller/product-option",
        },

        {
          icon: <IconMessage2 size={18} />,
          label: t('product_feedback'),
          key: "seller-product-feedback",
          path: "/seller/seller-product-feedback",
          permission: "manage_products",
        },
        {
          icon: <IconDiscount2 size={18} />,
          label: "Deals Manager",
          key: "deals-manager",
          path: "/seller/deals",
        },
        {
          icon: <IconTrash size={18} />,
          label: "Restore Products",
          key: "restore-products",
          path: "/seller/product-setup?view=trash",
        },
        {
          icon: <IconPackage size={18} />,
          label: t('product_reports'),
          key: "product-reports",
          path: "/seller/reports/products",
        },
      ],
    },
    {
      icon: <IconChartBar size={20} />,
      label: t('sales'),
      key: "sales",
      children: [
        {
          icon: <IconShoppingCart size={18} />,
          label: t('orders'),
          key: "orders",
          path: "/seller/orders",
        },
        {
          icon: <IconMessageCircle size={18} />,
          label: "Order Issues",
          key: "order-issues",
          path: "/seller/order-issues",
        },
        {
          icon: <IconArrowBackUp size={18} />,
          label: "Return Requests",
          key: "return-requests",
          path: "/seller/returns",
        },
        {
          icon: <IconCurrencyDollar size={18} />,
          label: "Earnings & Transfers",
          key: "seller-earnings",
          path: "/seller/earnings",
        },
        {
          icon: <IconChartBar size={18} />,
          label: t('sales_reports'),
          key: "sales-reports",
          path: "/seller/reports/sales",
        },
      ],
    },
    {
      icon: <IconArrowsExchange size={20} />,
      label: t('import_export'),
      key: "import-export",
      children: [
        {
          icon: <IconFileUpload size={18} />,
          label: t('import_data'),
          key: "import-data",
          path: "/seller/import",
        },
        {
          icon: <IconFileDownload size={18} />,
          label: t('export_data'),
          key: "export-data",
          path: "/seller/export",
        },
      ],
    },
    {
      icon: <IconUser size={20} />,
      label: t('account'),
      key: "profile-menu",
      children: [
        {
          icon: <IconUserCircle size={18} />,
          label: t('personal_info'),
          key: "profile",
          path: "/seller/profile?tab=personal",
        },
        {
          icon: <IconMapPin size={18} />,
          label: t('my_addresses'),
          key: "seller-addresses",
          path: "/seller/profile?tab=address",
        },
        {
          icon: <IconShieldLock size={18} />,
          label: t('profile_settings'),
          key: "seller-settings",
          path: "/seller/profile?tab=settings",
        },
      ],
    },

  ];



  const currentItems = sellerItems;

  return (
    <div className="layout">
      {isMobile ? <Navbar /> : <TopNavSuperAdmin onToggle={toggleSideNav} />}
      <div className={`main-content ${isSideNavOpen ? 'sidenav-open' : ''}`}>
        <div className="sidenav-overlay" onClick={() => setIsSideNavOpen(false)}></div>
        <div className="sidenav-container">
          <RoleToggle />
          <SideNav isOpen={isSideNavOpen} setIsOpen={setIsSideNavOpen} items={currentItems} />
        </div>
        <div className="page-content">
          <Outlet />
        </div>
      </div>
      <ChatBot />
    </div>
  );
};

export default SellerLayout;
